import {
  CheckBox,
  FieldSet,
  FormContext,
  FormState,
  FormValues,
  InfoBlockLayout,
  InfoBlockStatus,
  Select,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import * as Messages from "../../codegen/Messages";
import { IntegerInput } from "../../components/IntegerInput/IntegerInput";
import {
  mdsBackupPolicyDefaultRetentionInDays,
  mdsBackupPolicyMaxRetentionInDays,
  mdsBackupPolicyMinRetentionInDays,
  mysqlBackupSlots,
} from "../../helpers/resourceHelper";
import { mdsMinVersionSupportingPitrPolicy } from "../../helpers/validationHelper";

export const GROUP_MANAGEMENT = "management";

export const enum Fields {
  Backups = "mdsDbSystemDeploymentDetails.backupPolicy.isEnabled",
  CrashRecovery = "mdsDbSystemDeploymentDetails.crashRecovery",
  DeleteProtected = "mdsDbSystemDeploymentDetails.deletionPolicy.isDeleteProtected",
  RequireFinalBackup = "mdsDbSystemDeploymentDetails.deletionPolicy.finalBackup",
  RetainAutomaticBackups = "mdsDbSystemDeploymentDetails.deletionPolicy.automaticBackupRetention",
  RetentionPeriod = "mdsDbSystemDeploymentDetails.backupPolicy.retentionInDays",
  WindowStartTime = "mdsDbSystemDeploymentDetails.backupPolicy.windowStartTime",
  PitrPolicy = "mdsDbSystemDeploymentDetails.backupPolicy.pitrPolicy",
}

export const ManagementTabContent = (): JSX.Element => {
  const [enableAutomaticBackupsKey, setEnableAutomaticBackupsKey] = React.useState<string>(uniqueGUID());
  const [crashRecoveryInfoKey, setCrashRecoveryInfoKey] = React.useState<string>(uniqueGUID());
  const [showBackupOptions, setShowBackupOptions] = React.useState(true);
  const [enableCrashRecovery, setEnableCrashRecovery] = React.useState(true);
  const form: FormState = React.useContext(FormContext);
  const [defaultWindowStartTime, setWindowStartTime] = React.useState<string>();
  const [defaultRetentionPeriod, setDefaultRetentionPeriod] = React.useState(mdsBackupPolicyDefaultRetentionInDays);
  const [defaultPitrPolicy, setDefaultPitrPolicy] = React.useState<boolean>(true);
  const info = {
    messageType: InfoBlockStatus.INFO,
    message: Messages.createMysql.managementTab.info.backupInfo(),
    infoLayout: InfoBlockLayout.Compact,
  };
  const crashRecoveryInfo = {
    messageType: InfoBlockStatus.INFO,
    message: Messages.createMysql.managementTab.info.crashRecoveryInfo(),
    infoLayout: InfoBlockLayout.Compact,
  };

  const onChangeShowBackUpOptions = (value: boolean): void => {
    setShowBackupOptions(value);
    if (value) {
      const formValues: FormValues = form.getGroupValues(GROUP_MANAGEMENT);
      setDefaultRetentionPeriod(formValues[Fields.RetentionPeriod] || defaultRetentionPeriod);
      setDefaultPitrPolicy(formValues[Fields.PitrPolicy]);
      setWindowStartTime(formValues[Fields.WindowStartTime]?.length > 0
        ? formValues[Fields.WindowStartTime]?.[0].id : undefined);
      // if crash recovery is not enabled
      if (!enableCrashRecovery) {
        setEnableCrashRecovery(value);
        setCrashRecoveryInfoKey(uniqueGUID());
      }
    }
  };

  const windowStartTimeOptions = mysqlBackupSlots.map(slot => ({
    id: slot,
    text: `${slot} ${Messages.common.utc()}`,
  }));

  return (
    <>
      <FieldSet
        header={Messages.createMysql.managementTab.sectionTitles.backupHeader()}
      >
        <CheckBox
          key={enableAutomaticBackupsKey}
          defaultValue={showBackupOptions}
          statusInfo={info}
          fieldName={Fields.Backups}
          label={Messages.labels.enableAutomaticBackups()}
          tooltip={Messages.hints.tooltipMySQLEnableAutomaticBackups(
            Messages.validation.versionValidationMysql(mdsMinVersionSupportingPitrPolicy),
          )}
          onChange={value => onChangeShowBackUpOptions(value)}
        />
        {showBackupOptions && (
          <>
            <IntegerInput
              fieldName={Fields.RetentionPeriod}
              label={Messages.labels.backupRetentionPeriod()}
              tooltip={Messages.hints.tooltipMySQLBackupRetentionPeriod()}
              defaultValue={defaultRetentionPeriod}
              min={mdsBackupPolicyMinRetentionInDays}
              max={mdsBackupPolicyMaxRetentionInDays}
              required
            />
            <Select
              defaultValue={defaultWindowStartTime ? [defaultWindowStartTime] : undefined}
              required
              fieldName={Fields.WindowStartTime}
              label={Messages.labels.mySQLWindowStartTime()}
              tooltip={Messages.hints.tooltipMySQLWindowStartTime()}
              options={windowStartTimeOptions}
              hideSearchBox
            />
            <CheckBox
              fieldName={Fields.PitrPolicy}
              label={Messages.labels.pitrPolicy()}
              tooltip={Messages.hints.enablePitrPolicy()}
              defaultValue={defaultPitrPolicy}
            />
          </>
        )}
      </FieldSet>
      <FieldSet
        header={Messages.createMysql.managementTab.sectionTitles.deletionPlanHeader()}
      >
        <CheckBox
          fieldName={Fields.DeleteProtected}
          label={Messages.labels.mySQLDeleteProtected()}
          tooltip={Messages.hints.tooltipMySQLDeleteProtected()}
        />
        <CheckBox
          fieldName={Fields.RetainAutomaticBackups}
          label={Messages.labels.mySQLRetainAutomaticBackups()}
          tooltip={Messages.hints.tooltipMySQLRetainAutomaticBackups()}
        />
        <CheckBox
          fieldName={Fields.RequireFinalBackup}
          label={Messages.labels.mySQLRequireFinalBackup()}
          tooltip={Messages.hints.tooltipMySQLRequireFinalBackup()}
        />
      </FieldSet>
      <FieldSet
        header={Messages.createMysql.managementTab.sectionTitles.crashRecoveryHeader()}
      >
        <CheckBox
          key={crashRecoveryInfoKey}
          statusInfo={crashRecoveryInfo}
          defaultValue={enableCrashRecovery}
          onChange={(value: boolean) => {
            setEnableCrashRecovery(value);
            if (!value && showBackupOptions) {
              setShowBackupOptions(value);
              setEnableAutomaticBackupsKey(uniqueGUID());
            }
          }}
          fieldName={Fields.CrashRecovery}
          label={Messages.labels.mySQLEnableCrashRecovery()}
          tooltip={Messages.hints.tooltipMySQLCrashRecovery()}
        />
      </FieldSet>
    </>
  );
};
